import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Wallet, ArrowRight, Info } from 'lucide-react';
import './SimplisticCashLimitNotice.css';
import { useSimplisticTheme } from './SimplisticThemeContext';

export default function SimplisticCashLimitNotice({ onClose, cashLimit, cashSubtotal, currencySymbol = '' }) {
  const { theme } = useSimplisticTheme();
  const [confirmed, setConfirmed] = useState(false);
  const dialogRef = useRef(null);
  const continueRef = useRef(null);
  const limit = Math.max(0, Number(cashLimit) || 0);
  const selected = Math.max(0, Number(cashSubtotal) || 0);
  const excess = Math.max(0, selected - limit);
  const money = value => `${currencySymbol}${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  useEffect(() => {
    const previousFocus = document.activeElement;
    dialogRef.current?.focus();
    return () => { if (previousFocus?.isConnected) previousFocus.focus(); };
  }, []);

  const continueToBuylist = () => { if (confirmed) onClose(true); };
  const onKeyDown = event => {
    // The surrounding cart drawer must not handle this dialog's keyboard input.
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      continueToBuylist();
    }
    if (event.key !== 'Tab') return;
    const controls = [...dialogRef.current.querySelectorAll('input, button:not(:disabled)')];
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first?.focus();
    }
  };

  return createPortal(
    <div className="simple-cash-limit-overlay simple-themed-overlay" data-simple-theme={theme} onClick={event => event.stopPropagation()}>
      <section ref={dialogRef} className="simple-cash-limit-dialog" role="dialog" aria-modal="true" aria-labelledby="simple-cash-limit-title" aria-describedby="simple-cash-limit-description" tabIndex={-1} onKeyDown={onKeyDown}>
        <header className="simple-cash-limit-heading">
          <span className="simple-cash-limit-icon" aria-hidden="true"><Wallet size={23} strokeWidth={1.8} /></span>
          <span className="simple-cash-limit-eyebrow">PAYMENT NOTICE</span>
          <h2 id="simple-cash-limit-title">Your selection is over the cash limit</h2>
          <p id="simple-cash-limit-description">Your cash-selected items are <strong>{money(excess)} over</strong> this store’s cash limit.</p>
        </header>

        <dl className="simple-cash-limit-totals">
          <div><dt>Cash selected</dt><dd>{money(selected)}</dd></div>
          <div><dt>Store cash limit</dt><dd>{money(limit)}</dd></div>
          <div className="simple-cash-limit-excess"><dt>Amount over the limit</dt><dd>{money(excess)}</dd></div>
        </dl>

        <div className="simple-cash-limit-next"><Info size={18} aria-hidden="true" /><p>Return to your buylist to switch items to store credit or remove items. <strong>Continuing won’t change your payment selections.</strong></p></div>

        <footer className="simple-cash-limit-footer">
          <label className={`simple-cash-limit-confirm${confirmed ? ' is-confirmed' : ''}`}>
            <input type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)} />
            <span>I understand my selection exceeds the cash limit.</span>
          </label>
          <button ref={continueRef} type="button" className="simple-cash-limit-continue" disabled={!confirmed} onClick={continueToBuylist}>Continue to my buylist<ArrowRight size={17} aria-hidden="true" /></button>
          <p className="simple-cash-limit-hint">You can review your payment choices in the cart.</p>
        </footer>
      </section>
    </div>, document.body
  );
}
